/**
 * Player shell — the surface behind `#/play…`, rendered by App.tsx instead
 * of the GM chrome whenever `isPlayerRoute(ui.route)`.
 *
 * WHAT IS HERE vs WHAT IS ABSENT: nav is limited to the four player
 * destinations (content browser, rules, character creation, my characters).
 * The GM-only views — notes, NPCs, sessions/prep, encounters, world, story,
 * map forge, party, world reference and the GM screen — are ABSENT: not
 * rendered, not disabled, not hidden. This file is the only place the two
 * surfaces diverge, so "absent" is structural: there is no branch here that
 * can produce a GM view, and no GM view module is ever imported.
 *
 * WHY IT IS SAFE ON THE GM'S OWN MACHINE: everything this shell reads and
 * writes goes through the `atla-player:*` storage namespace, which the hash
 * route prefix selected at boot (see the module comment in
 * persistence/storage.ts — screen-share leak rationale, flush-then-swap on
 * crossings). Characters built here autosave locally until the player
 * exports the file (CharacterTransferBar → .atlachar.json / share code) and
 * sends it to the GM — file bytes cross the namespaces, campaign data never
 * does.
 *
 * NO CAMPAIGN YET: the player surface starts empty (it cannot see the GM's
 * document), so the home / create / characters views offer a one-click
 * starter document in the player's own namespace instead of the cockpit's
 * "no campaign" card — that card links back to `cockpit`, a GM route, and
 * must never appear under `#/play`.
 */
import { lazy, Suspense, type ReactElement } from 'react';
import { RULE_SYSTEMS, type RuleSystemId } from '@/domain/campaign';
import { useT } from '@/i18n';
import {
  createCampaign,
  navigate,
  useAppState,
  type PlayerRouteId,
} from '@/state/store';
import ErrorBoundary from '@/ui/ErrorBoundary';

const BuilderLauncher = lazy(() => import('@/ui/views/BuilderLauncher'));
const CharactersView = lazy(() => import('@/ui/views/CharactersView'));
const ContentView = lazy(() => import('@/ui/views/ContentView'));
const RulesView = lazy(() => import('@/ui/views/RulesView'));

interface PlayerNavItem {
  id: PlayerRouteId;
  labelKey: string;
}

/** The FOUR player destinations — GM sections simply do not exist here. */
const PLAYER_NAV: PlayerNavItem[] = [
  { id: 'play/content', labelKey: 'player.nav.content' },
  { id: 'play/rules', labelKey: 'player.nav.rules' },
  { id: 'play/create', labelKey: 'player.nav.create' },
  { id: 'play/characters', labelKey: 'player.nav.characters' },
];

const START_ORDER: RuleSystemId[] = ['fate', 'dnd5e-atla', 'mixed'];

export default function PlayerShell(): ReactElement {
  const { ui } = useAppState();
  const t = useT();

  return (
    <div className="shell player-shell">
      <header className="topbar player-topbar">
        <div>
          <h1>
            <a className="player-home" href="#/play">
              {t('player.title')}
            </a>
          </h1>
          <div className="subtitle">{t('player.subtitle')}</div>
        </div>
        <div className="spacer" />
        <div className="player-head-links">
          <a className="btn secondary player-back" href="#/">
            {t('player.backToGm')}
          </a>
        </div>
      </header>

      <nav className="sidebar" aria-label={t('player.navTitle')}>
        <div className="nav-section">{t('player.navTitle')}</div>
        {PLAYER_NAV.map((item) => (
          <button
            key={item.id}
            type="button"
            data-route={item.id}
            className={`nav-btn${ui.route === item.id ? ' active' : ''}`}
            onClick={() => navigate(item.id)}
          >
            <span>{t(item.labelKey)}</span>
          </button>
        ))}
      </nav>

      <main className="main">
        <ErrorBoundary key={ui.route}>
          <Suspense
            fallback={
              <section className="card" aria-busy="true">
                <p className="muted">…</p>
              </section>
            }
          >
            {ui.route === 'play' ? (
              <PlayerHome />
            ) : ui.route === 'play/content' ? (
              <ContentView />
            ) : ui.route === 'play/rules' ? (
              <RulesView />
            ) : ui.route === 'play/create' ? (
              <PlayerCreate />
            ) : (
              <PlayerCharacters />
            )}
          </Suspense>
        </ErrorBoundary>
      </main>

      {ui.toastKey ? (
        <div className="toast">{t(ui.toastKey, ui.toastParams ?? undefined)}</div>
      ) : null}
    </div>
  );
}

/** `#/play` — orientation, quick links, and the first-run start card. */
function PlayerHome(): ReactElement {
  const { campaign } = useAppState();
  const t = useT();
  return (
    <section className="card player-welcome">
      <h2>{t('player.homeTitle')}</h2>
      <p className="muted">{t('player.homeBody')}</p>
      {campaign ? null : <PlayerStartCard />}
      <div className="player-home-links">
        {PLAYER_NAV.map((item) => (
          <button
            key={item.id}
            type="button"
            className="btn secondary"
            onClick={() => navigate(item.id)}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * First run on the player surface: there is no campaign document yet (the
 * GM's is in another namespace), so the player picks the rule system their
 * table plays. The starter document lives in `atla-player:*` like everything
 * else here.
 */
function PlayerStartCard(): ReactElement {
  const { ui } = useAppState();
  const t = useT();
  const name = ui.locale === 'tr' ? 'Oyuncu Dosyası' : 'Player File';
  return (
    <section className="card start-card">
      <h3>{t('player.startTitle')}</h3>
      <p className="muted">{t('player.startBody')}</p>
      <div className="btn-row">
        {START_ORDER.map((system) => (
          <button
            key={system}
            type="button"
            className="btn"
            onClick={() => createCampaign(name, system)}
          >
            {ui.locale === 'tr'
              ? RULE_SYSTEMS[system].label.tr
              : RULE_SYSTEMS[system].label.en}
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * `#/play/create` — the builder's rules chooser (Mixed race + class, D&D 5e
 * ATLA or Fate), with the starter document's system marked: a player whose
 * starter file was made for one system can still build for another.
 */
function PlayerCreate(): ReactElement {
  const { campaign } = useAppState();
  if (!campaign) return <PlayerStartCard />;
  const toList = (): void => navigate('play/characters');
  return <BuilderLauncher campaignSystem={campaign.ruleSystem} era={campaign.era} onClose={toList} />;
}

/**
 * `#/play/characters` (+ `:id` through the store's selectedId — the router
 * parses `#/play/characters/:id` into the same route with a selection).
 */
function PlayerCharacters(): ReactElement {
  const { campaign } = useAppState();
  if (!campaign) return <PlayerStartCard />;
  return <CharactersView />;
}
